<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/Generic_Types/The_TyperExample_DSL';
    let nextLink= '/Examples/External_Components';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="generic-types--example-typer-definition-1"
  bind:intersecting={visibleById["generic-types--example-typer-definition-1"]}
  onIntersectChange={(visible) => setVisible("generic-types--example-typer-definition-1", visible)}
>Generic Types — Example Typer Definition</SectionComponent>
<p>This example shows how <strong>generic types</strong> and <strong>units of measurement</strong> can be defined.</p>
<SectionComponent tag="h2" 
  id="the-typer-definition-of-the-example-language-2"
  bind:intersecting={visibleById["the-typer-definition-of-the-example-language-2"]}
  onIntersectChange={(visible) => setVisible("the-typer-definition-of-the-example-language-2", visible)}
>The Typer Definition of the Example Language</SectionComponent>
<p>Once the structure of the example language is defined, we can focus on its <strong>typer definition</strong>.<br>
In Freon, types do <strong>not</strong> reside inside the AST—they are defined separately.<br>
We therefore need to associate a <strong>type concept</strong> with each relevant AST concept.</p>
<h3 id="the-type-concepts-1">The Type Concepts</h3>
<p>First, we define type concepts that correspond to the AST’s <code>GenericType</code> and <code>UnitOfMeasurement</code>.<br>
They are similar to their AST counterparts, but note that here we use <strong>FreType</strong> as the property type.
We want to build a <strong>hierarchy of type concepts</strong>, not AST nodes.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.type#L6-L14

type TT_GenericType &#123;
    base: FreType;
    kind: GenericKind;
&#125;

type TT_MeasurementType &#123;
    base: FreType;
    unit: UnitKind;
&#125;</code></pre>
<h3 id="the-infertype-rules-2">The Infertype Rules</h3>
<p>Next, we associate AST nodes with their corresponding type concepts using <strong>infertype</strong> rules.<br>
When invoked, these rules create new instances of the type concepts.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.type#L34-L46

GenericType &#123;
    infertype TT_GenericType &#123;
        base: typeof(self.baseType),
        kind: self.kind
    &#125;;
&#125;

UnitOfMeasurement &#123;
    infertype TT_MeasurementType &#123;
        base: typeof(self.baseType),
        unit: self.unit
    &#125;;
&#125;</code></pre>
<h3 id="the-conforms-and-equals-rules-3">The Conforms and Equals Rules</h3>
<p>To compare expression types with declared types, we define <strong>equalsto</strong> and <strong>conformsto</strong> rules.</p>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.type#L91-L118

TT_GenericType &#123;
    equalsto x:TT_GenericType where &#123;
            x.base equalsto self.base;
            x.kind equalsto self.kind;
        &#125;;
    conformsto other:TT_GenericType where &#123;
            // both conditions must be true
            self.base conformsto other.base;
            self.kind conformsto other.kind;
        &#125;;
&#125;

GenericKind &#123;
    Set conformsto #GenericKind:Collection;
    Sequence conformsto #GenericKind:Collection;
    Bag conformsto #GenericKind:Collection;
&#125;

TT_MeasurementType &#123;
    equalsto aap:TT_MeasurementType where &#123;
            aap.base equalsto self.base;
            aap.unit equalsto self.unit;
        &#125;;
    conformsto rr:TT_MeasurementType where &#123;
            self.base conformsto rr.base;
            self.unit equalsto rr.unit;
        &#125;;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-complete-typer-definition-3"
  bind:intersecting={visibleById["the-complete-typer-definition-3"]}
  onIntersectChange={(visible) => setVisible("the-complete-typer-definition-3", visible)}
>The Complete Typer Definition</SectionComponent>
<pre class="language-freon"><code class="language-freon">// TyperExample/src/defs/projectY.type

typer

// What are types?
istype &#123; TopType &#125;

type TT_GenericType &#123;
    base: FreType;
    kind: GenericKind;
&#125;

type TT_MeasurementType &#123;
    base: FreType;
    unit: UnitKind;
&#125;

// Which concepts have a type?
hastype &#123; Exp, TypeUsage &#125;

// What are the top and bottom types?
anytype &#123;
    conformsto #PredefinedType:ANY; // PredefinedType:ANY is the least specific type
&#125;

PredefinedType &#123;
    NULL conformsto anytype; // PredefinedType:NULL is the most specific type
    NUMBER conformsto #PredefinedType:STRING;
&#125;

// Which type does an expression have?
TypeRef &#123;
    infertype self.type;
&#125;

GenericType &#123;
    infertype TT_GenericType &#123;
        base: typeof(self.baseType),
        kind: self.kind
    &#125;;
&#125;

UnitOfMeasurement &#123;
    infertype TT_MeasurementType &#123;
        base: typeof(self.baseType),
        unit: self.unit
    &#125;;
&#125;

NumberLiteral &#123;
    infertype #PredefinedType:NUMBER;
&#125;

StringLiteral &#123;
    infertype #PredefinedType:STRING;
&#125;

BooleanLiteral &#123;
    infertype #PredefinedType:BOOLEAN;
&#125;

NamedExp &#123;
    infertype self.myType;
&#125;

PlusExp &#123;
    infertype commonSuperType(self.left, self.right);
&#125;

UnitLiteral &#123;
    // 62 kilogram, or 112 miles
    infertype TT_MeasurementType &#123;
                  base: typeof(self.inner),
                  unit: self.unit
              &#125;;
&#125;

GenericLiteral &#123;
    // Set&#123; 12, 14, 16, 18 &#125;
    infertype TT_GenericType &#123;
        base: typeof(self.content),
        kind: self.kind
    &#125;;
&#125;

// Which types are 'equal' to each other?
NamedType &#123;
    equalsto aa:NamedType where &#123;
            aa.name equalsto self.name;
        &#125;;
&#125;

TT_GenericType &#123;
    equalsto x:TT_GenericType where &#123;
            x.base equalsto self.base;
            x.kind equalsto self.kind;
        &#125;;
    conformsto other:TT_GenericType where &#123;
            // both conditions must be true
            self.base conformsto other.base;
            self.kind conformsto other.kind;
        &#125;;
&#125;

GenericKind &#123;
    Set conformsto #GenericKind:Collection;
    Sequence conformsto #GenericKind:Collection;
    Bag conformsto #GenericKind:Collection;
&#125;

TT_MeasurementType &#123;
    equalsto aap:TT_MeasurementType where &#123;
            aap.base equalsto self.base;
            aap.unit equalsto self.unit;
        &#125;;
    conformsto rr:TT_MeasurementType where &#123;
            self.base conformsto rr.base;
            self.unit equalsto rr.unit;
        &#125;;
&#125;
</code></pre>


<PrevNextSection {prevLink} {nextLink} />